<mat-form-field class="full-width m-b-20" appearance="outline" subscriptSizing="dynamic"
    [class.ng-invalid]="isRequired && !icdChips.length"
    [class.ng-required]="isRequired">
    <mat-label>ICD-10 Codes</mat-label>
    <!-- Drag to reorder. The FIRST code is the primary diagnosis, so this is not cosmetic —
         dragging a code to the front is what makes it primary on the claim. `cdkDropListOrientation`
         is horizontal because chips wrap in rows. -->
    <!-- `dynamic` so the subscript area grows to fit the hint instead of holding a fixed
         single-line box. The hint wraps to two lines in a dialog, and the fixed box let the
         second line overlap the findings panel below it. -->
    <mat-chip-grid #chipGrid aria-label="Enter ICD-10 Codes" cdkDropList
        cdkDropListOrientation="horizontal" (cdkDropListDropped)="dropChip($event)">
        <mat-chip-row *ngFor="let icd of icdChips; let i = index" (removed)="removeChip(i)" [editable]="false"
            (edited)="editChip(icd, $event)" [aria-description]="'press enter to edit ' + icd.code"
            cdkDrag [cdkDragData]="icd" class="icd-chip" appTouchEdit
            [ngClass]="'icd-state--' + (chipViews[i].primaryState || 'none')"
            [class.icd-state--primary]="i === 0">
            <!-- Position is meaning: the first chip is the primary diagnosis, and several rules
                 are about that position specifically. -->
            <span class="icd-primary-marker" *ngIf="i === 0" matTooltip="Primary diagnosis">1&deg;</span>
            <!-- Same three parts, same classes, same order as the patient file card. Only the
                 description gives way under pressure — see the `min-width: 0` chain in
                 `src/_icd-chip.scss`, which is what keeps the date and the state icons visible
                 however long the description runs. -->
            <span class="icd-chip-code" (dblclick)="editDate(i)"
                [matTooltip]="chipTooltips[i]" matTooltipClass="icd-tooltip">{{ icd.code }}</span>
            <span class="icd-chip-desc" *ngIf="icd.description" (dblclick)="editDate(i)"
                [matTooltip]="chipTooltips[i]" matTooltipClass="icd-tooltip">{{ icd.description }}</span>
            <!-- Sibling of the label, not a child. An undated code says so rather than looking
                 the same as a dated one. -->
            <span class="icd-chip-date" [class.unset]="!icd.onsetDate" (dblclick)="editDate(i)"
                matTooltip="Double-click or tap and hold to set the onset date">
                {{ chipViews[i].onsetLabel || 'no date' }}
            </span>
            <!-- One marker per state, so a PMB-linked code the pipeline also flagged shows both
                 rather than the loudest. Icons as well as colour — these carry billing
                 consequences and hue alone is not something every clinician can rely on. -->
            <mat-icon class="icd-state-icon" *ngFor="let state of chipViews[i].iconStates"
                [ngClass]="state" [matTooltip]="chipViews[i].tooltip"
                matTooltipClass="icd-tooltip" [ngSwitch]="state">
                <ng-container *ngSwitchCase="'error'">error_outline</ng-container>
                <ng-container *ngSwitchCase="'warning'">warning_amber</ng-container>
                <ng-container *ngSwitchCase="'flagged'">flag</ng-container>
                <ng-container *ngSwitchCase="'ai-applied'">auto_awesome</ng-container>
                <ng-container *ngSwitchCase="'pmb'">shield</ng-container>
            </mat-icon>
            <button matChipRemove [attr.aria-label]="'remove ' + icd.code">
                <mat-icon>cancel</mat-icon>
            </button>
        </mat-chip-row>
        <input #inputElement placeholder="New ICD-10 Code..." [matChipInputFor]="chipGrid"
            [matChipInputSeparatorKeyCodes]="separatorKeysCodes" [matChipInputAddOnBlur]="addOnBlur"
            [formControl]="icdControl" [matAutocomplete]="auto" />
    </mat-chip-grid>
    <!-- Shortened to one line at dialog width. The long version wrapped, and a two-line hint in
         a fixed subscript box was what pushed into the findings panel below. -->
    <mat-hint class="hint">
        First code is primary - drag to reorder. Double-click or tap and hold a chip for its onset date.
    </mat-hint>
    <mat-autocomplete #auto="matAutocomplete" (optionSelected)="addChip($event.option.value)">
        <mat-option *ngFor="let code of filteredIcdCodes | async" [value]="code" [class.pmb]="code.pmbCodes?.length">
            {{ code.code }} - {{ code.description }}
            <!-- Flagged in the LIST, before it is picked. Telling a clinician a code was wrong
                 only after they chose it wastes the click and reads as the system nit-picking. -->
            <span class="option-flag error" *ngIf="code.validClinicalUse === false"
                matTooltip="Category heading — a more specific code is required">needs detail</span>
            <span class="option-flag error" *ngIf="code.isDiscontinued">withdrawn</span>
            <span class="option-flag" *ngIf="code.validPrimary === false && code.validClinicalUse !== false">
                not primary
            </span>
            <span class="option-flag" *ngIf="code.gender">{{ code.gender === 'F' ? 'female only' : 'male only' }}</span>
        </mat-option>
    </mat-autocomplete>
</mat-form-field>

<!-- ══ Coding findings ═══════════════════════════════════════════════════════════════
     Advisory only. Nothing here blocks a save — the MIT's flags are non-exhaustive and the
     codebook is a 2021 snapshot, so a clinician with the patient in front of them can be
     right when the table is wrong. The REASON is the point, not the verdict. -->
<div class="icd-findings" *ngIf="findings.length" [ngClass]="overallStatus">
    <div class="finding" *ngFor="let f of findings" [ngClass]="f.severity">
        <mat-icon class="finding-icon">
            {{ f.severity === 'error' ? 'error_outline' : f.severity === 'warning' ? 'warning_amber' : 'info_outline' }}
        </mat-icon>
        <div class="finding-body">
            <span class="finding-message">{{ f.message }}</span>
            <span class="finding-suggestion" *ngIf="f.suggestion">{{ f.suggestion }}</span>
        </div>
        <!-- The one-click fix for the commonest finding: the code that can lead is not first. -->
        <button mat-button class="finding-action" *ngIf="f.ruleId === 'icd-011' && f.codeIndex"
            (click)="makePrimary(f.codeIndex)">
            Make primary
        </button>
    </div>
</div>